/**
 * BlobRefView — renders a tool result that was offloaded to Firebase Storage
 * during persist-time compaction.  The inline record only carries a tiny
 * preview; the full payload lives at `.messages/{...}.json` and is lazily
 * fetched on demand.
 *
 * Auto-expands when the full payload is small (< AUTO_FETCH_BYTES).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import { api } from '@/services/api';
import { useThreadState } from './AiChatProvider';

export interface BlobRef {
  _blobRef: string;
  _offloadedAt?: string;
  preview: string;
  totalBytes: number;
  originalType?: 'text' | 'json';
  summary?: string;
  _overflowFailed?: boolean;
  _reason?: string;
}

export function isBlobRefValue(value: unknown): value is BlobRef {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v._blobRef === 'string' && typeof v.totalBytes === 'number';
}

/** Walk nested `{ type, value }` envelopes to find an embedded BlobRef. */
export function extractBlobRef(value: unknown): BlobRef | null {
  if (!value) return null;
  if (isBlobRefValue(value)) return value;
  if (typeof value === 'object' && 'value' in (value as any)) {
    const inner = (value as any).value;
    if (isBlobRefValue(inner)) return inner;
  }
  return null;
}

const AUTO_FETCH_BYTES = 10_000;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function safeJSONStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function BlobRefView({ ref, threadIdOverride }: { ref: BlobRef; threadIdOverride?: string | null }) {
  const { currentThreadId } = useThreadState();
  const threadId = threadIdOverride ?? currentThreadId;

  const [fullText, setFullText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const didAutoFetch = useRef(false);

  const fetchBlob = useCallback(async () => {
    if (!threadId || loading || fullText !== null) return;
    if (!ref._blobRef) {
      setError(ref._reason ?? 'no blob ref');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ path: string; bytes: number; content: string; parsed: unknown }>(
        `/chat/threads/${threadId}/blob`,
        { path: ref._blobRef },
      );
      // Prefer the parsed value when the blob was JSON — pretty-print it for display.
      const body = data.parsed !== null && data.parsed !== undefined
        ? safeJSONStringify(data.parsed)
        : (typeof data.content === 'string' ? data.content : JSON.stringify(data.content));
      setFullText(body);
    } catch (err: any) {
      setError(err?.message ?? 'failed to fetch blob');
    } finally {
      setLoading(false);
    }
  }, [threadId, ref._blobRef, ref._reason, loading, fullText]);

  useEffect(() => {
    if (didAutoFetch.current) return;
    if (ref.totalBytes > 0 && ref.totalBytes <= AUTO_FETCH_BYTES) {
      didAutoFetch.current = true;
      fetchBlob();
    }
  }, [ref.totalBytes, fetchBlob]);

  const showFull = fullText !== null;
  const isFailed = ref._overflowFailed;

  return (
    <div className={`rounded-md border border-zinc-200 bg-zinc-50`}>
      <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
        <span className={`font-medium ${isFailed ? 'text-red-700' : 'text-zinc-600'}`}>
          {isFailed ? 'Overflow failed' : 'Offloaded result'}
        </span>
        {ref.summary && <span className="text-zinc-500 truncate">· {ref.summary}</span>}
        <span className="text-zinc-400 ml-auto">{fmtBytes(ref.totalBytes)}</span>
        <button
          type="button"
          className="text-zinc-500 hover:text-zinc-700"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-zinc-200 px-2.5 py-2">
          {isFailed && ref._reason && (
            <div className="mb-1.5 text-xs text-red-600">{ref._reason}</div>
          )}

          {!showFull && ref.preview && (
            <pre className="text-xs text-zinc-700 font-mono whitespace-pre-wrap break-words max-h-[160px] overflow-y-auto">
              {ref.preview}
              {ref.totalBytes > ref.preview.length && (
                <span className="text-zinc-400">
                  {'\n'}… truncated preview. Full payload is {fmtBytes(ref.totalBytes)}.
                </span>
              )}
            </pre>
          )}

          {showFull && (
            <pre className="text-xs text-zinc-700 font-mono whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto">
              {fullText}
            </pre>
          )}

          {!isFailed && !showFull && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={fetchBlob}
                disabled={loading || !threadId}
                className="inline-flex items-center gap-1.5 rounded-md bg-white border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-100 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {loading ? 'Loading…' : `Show full (${fmtBytes(ref.totalBytes)})`}
              </button>
              {error && <span className="ml-2 text-xs text-red-600">{error}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
